import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { getCollection } from '@adornis/baseql/server/collections.js';

@Entity()
export class RatingEntity extends MongoEntity {
  static override _class = 'RatingEntity';
  static override _collectionName = 'RatingEntity';

  @Field(() => String) tag: Maybe<string>;
  @Field(() => Date) createdAt: Maybe<Date>;
  @Field(() => Float) rating: Maybe<number>;
  @Field(() => Int) totalCount: Maybe<number>;
  @Field(() => Int) countAbove5: Maybe<number>;
  @Field(() => Int) countAbove7: Maybe<number>;
  @Field(() => String) filterKey: Maybe<string>;

  static override async beforeCreate(entity: RatingEntity) {
    entity.createdAt = new Date();
    await super.beforeCreate(entity);
  }
}

export async function upsertTag(
  tagName: string,
  rating: number,
  filterKey: string,
  totalCount: number,
  countAbove5: number,
  countAbove7: number,
) {
  const collection = await getCollection<RatingEntity>(RatingEntity._class);

  collection.updateOne(
    { tag: tagName },
    {
      $set: { rating, totalCount, countAbove5, countAbove7 },
      $setOnInsert: { filterKey, _id: A.getGloballyUniqueID(), _class: RatingEntity._class, createdAt: new Date() },
    },
    { upsert: true },
  );
}

export async function getRatingByTag(tag: string): Promise<RatingEntity | null> {
  const collection = await getCollection<RatingEntity>(RatingEntity._class);
  const item = await collection.findOne({ tag: { $regex: tag, $options: 'i' } });
  if (!item) {
    logger.warn(`No RatingEntity found for tag ${tag}`);
    return null;
  }
  return new RatingEntity(item);
}

// Entities to represent the rating summary return shape from src/api/rating.ts

@Entity()
export class AverageRatingEntryEntity extends AdornisEntity {
  static override _class = 'AverageRatingEntryEntity';

  static override resolveID(entity: AverageRatingEntryEntity): string {
    return entity.key!;
  }

  @Field(() => String) key: Maybe<string>;
  @Field(() => Float) value: Maybe<number>;
}

@Entity()
export class RatingMapEntryEntity extends AdornisEntity {
  static override _class = 'RatingMapEntryEntity';

  static override resolveID(entity: RatingMapEntryEntity): string {
    return entity.key!;
  }

  @Field(() => String) key: Maybe<string>;
  @Field(() => Int) countTotal: Maybe<number>;
  @Field(() => Int) numberOfAbove5: Maybe<number>;
  @Field(() => Int) numberOfAbove7: Maybe<number>;
}

@Entity()
export class RatingSummaryEntity extends AdornisEntity {
  static override _class = 'RatingSummaryEntity';

  static override resolveID(entity: RatingSummaryEntity): string {
    return A.getGloballyUniqueID();
  }

  @Field(() => Int) totalCountAnsweringGlobal: Maybe<number>;
  @Field(() => Float) totalCountRatingGlobal: Maybe<number>;
  @Field(() => Int) totalCountRelevantGlobal: Maybe<number>;
  @Field(() => Int) totalCountGreaterEqual5Global: Maybe<number>;
  @Field(() => Int) totalCountGreaterEqual7Global: Maybe<number>;

  @Field(() => Int) totalCountAnsweringIntercom: Maybe<number>;
  @Field(() => Float) totalCountRatingIntercom: Maybe<number>;
  @Field(() => Int) totalCountRelevantIntercom: Maybe<number>;
  @Field(() => Int) totalCountGreaterEqual5Intercom: Maybe<number>;
  @Field(() => Int) totalCountGreaterEqual7Intercom: Maybe<number>;

  @Field(() => Int) totalCountAnsweringEmail: Maybe<number>;
  @Field(() => Float) totalCountRatingEmail: Maybe<number>;
  @Field(() => Int) totalCountRelevantEmail: Maybe<number>;
  @Field(() => Int) totalCountGreaterEqual5Email: Maybe<number>;
  @Field(() => Int) totalCountGreaterEqual7Email: Maybe<number>;

  @Field(() => [AverageRatingEntryEntity]) averageRatings: Maybe<AverageRatingEntryEntity[]>;
  @Field(() => Int) totalAnswerings: Maybe<number>;
  @Field(() => Float) totalRatings: Maybe<number>;
  @Field(() => [RatingMapEntryEntity]) ratingMap: Maybe<RatingMapEntryEntity[]>;

  constructor(doc: PartialEntityData<RatingSummaryEntity>) {
    super(doc);
  }
}
